/**
 * @file 表示用テキスト変換・ビジネスロジックの共通関数群
 *
 * 支払方法・請求ステータス・オプションステータスの表示用文字列変換、
 * VoIPサービスパック判定、ログイン状態判定、
 * ひらがな⇒カタカナ変換、日本円表記変換等のユーティリティ関数。
 * 画面全体から広く参照される。
 */
import { Constants, CodeAndName } from '@/utils/constants';
import { getCredentials } from './cognito';

/**
 * 支払い方法の表示用文字列を返す
 * @param type 支払い方法タイプ
 * @returns 表示用文字列
 */
export const getPaymentTypeText = (type: string): string => {
  const result: CodeAndName | undefined = Object.values(Constants.PaymentMethod).find(
    (item: CodeAndName): boolean => {
      return item.code === type;
    }
  );
  if (result === undefined) {
    return '読み込めません';
  }

  return 'nameShort' in result ? <string>result.nameShort : result.name;
};

/**
 * 「請求情報」の「支払状況」の表示用文字列を返却する。
 * @param status 請求状態コード
 * @returns 表示用文字列
 */
export const getPaymentStatusText = (status: string) => {
  switch (status) {
    case '0':
      return 'ご請求中';
    case '1':
      return 'ご請求中';
    case '2':
      return 'ご請求中';
    case '3':
      return 'お支払い済み';
    case '4':
      return 'ご請求中';
    case '5':
      return 'ご請求中';
    case '6':
      return 'ご請求中';
    default:
      return '読み込めません';
  }
};

/**
 * オプションの表示用文字列を返す
 * @param status オプション状態
 * @returns 表示用文字列
 */
export const getOptionStatusText = (status: string) => {
  switch (status) {
    case Constants.OptionStatus.NONE:
      return '未申込';
    case Constants.OptionStatus.ENTRY:
      return '申込中';
    case Constants.OptionStatus.SERVICE:
      return '利用中';
    case Constants.OptionStatus.CANCEL:
      return 'キャンセル';
    case Constants.OptionStatus.QUIT:
      return '解約済';
    case Constants.OptionStatus.QUIT_WITHOUT:
      return '解約中';
    default:
      return '読み込めません';
  }
};

/**
 * 単品付加サービス(番号表示)を含むサービスパックの判定
 * @param pack '01', '02', '03', '04', '05'
 * @returns 含まれる場合：true、含まれない場合：false
 */
export const containNumberDisplay = (pack: string): boolean => {
  return !!(pack === '01' || pack === '03' || pack === '05');
};

/**
 * 単品付加サービス(番号リクエスト)を含むサービスパックの判定
 * @param pack '01', '02', '03', '04', '05'
 * @returns 含まれる場合：true、含まれない場合：false
 */
export const containNumberRequest = (pack: string): boolean => {
  return !!(pack === '01' || pack === '03' || pack === '05');
};

/**
 * 単品付加サービス(キャッチ電話)を含むサービスパックの判定
 * @param pack '01', '02', '03', '04', '05'
 * @returns 含まれる場合：true、含まれない場合：false
 */
export const containCatchPhone = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02' || pack === '03' || pack === '04');
};

/**
 * 単品付加サービス(着信お断り)を含むサービスパックの判定
 * @param pack '01', '02', '03', '04', '05'
 * @returns 含まれる場合：true、含まれない場合：false
 */
export const containStopNuisanceCall = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02' || pack === '03' || pack === '04');
};

/**
 * 単品付加サービス(着信転送)を含むサービスパックの判定
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containVoiceWarp = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02');
};

/**
 * ログイン判定
 * @param pathName next/routerのpathname
 * @returns ログインしている場合：true、ログインしていない場合：false
 */
export const checkIsLogin = (pathName: string): boolean => {
  return (
    !Constants.PathList.NOT_LOGIN.some((path) => path.startsWith(pathName)) &&
    getCredentials() !== null
  );
};

/**
 * 全角ひらがなから全角カタカナに文字変換
 * @param param 変換前全角ひらがな文字列
 * @returns 変換後全角カタカナ文字列
 */
export const kana2HankakuKana = (param: string): string => {
  // 全角ひらがな → 全角カタカナ
  const zenKata = param.replace(/[\u3041-\u3096]/g, (match) =>
    String.fromCharCode(match.charCodeAt(0) + 0x60)
  );

  const zen = [
    'ガ',
    'ギ',
    'グ',
    'ゲ',
    'ゴ',
    'ザ',
    'ジ',
    'ズ',
    'ゼ',
    'ゾ',
    'ダ',
    'ヂ',
    'ヅ',
    'デ',
    'ド',
    'バ',
    'ビ',
    'ブ',
    'ベ',
    'ボ',
    'パ',
    'ピ',
    'プ',
    'ペ',
    'ポ',
    'ア',
    'イ',
    'ウ',
    'エ',
    'オ',
    'カ',
    'キ',
    'ク',
    'ケ',
    'コ',
    'サ',
    'シ',
    'ス',
    'セ',
    'ソ',
    'タ',
    'チ',
    'ツ',
    'テ',
    'ト',
    'ナ',
    'ニ',
    'ヌ',
    'ネ',
    'ノ',
    'ハ',
    'ヒ',
    'フ',
    'ヘ',
    'ホ',
    'マ',
    'ミ',
    'ム',
    'メ',
    'モ',
    'ヤ',
    'ユ',
    'ヨ',
    'ラ',
    'リ',
    'ル',
    'レ',
    'ロ',
    'ワ',
    'ヲ',
    'ン',
    'ァ',
    'ィ',
    'ゥ',
    'ェ',
    'ォ',
    'ャ',
    'ュ',
    'ョ',
    'ッ',
    'ー',
  ];
  const han = [
    'ｶﾞ',
    'ｷﾞ',
    'ｸﾞ',
    'ｹﾞ',
    'ｺﾞ',
    'ｻﾞ',
    'ｼﾞ',
    'ｽﾞ',
    'ｾﾞ',
    'ｿﾞ',
    'ﾀﾞ',
    'ﾁﾞ',
    'ﾂﾞ',
    'ﾃﾞ',
    'ﾄﾞ',
    'ﾊﾞ',
    'ﾋﾞ',
    'ﾌﾞ',
    'ﾍﾞ',
    'ﾎﾞ',
    'ﾊﾟ',
    'ﾋﾟ',
    'ﾌﾟ',
    'ﾍﾟ',
    'ﾎﾟ',
    'ｱ',
    'ｲ',
    'ｳ',
    'ｴ',
    'ｵ',
    'ｶ',
    'ｷ',
    'ｸ',
    'ｹ',
    'ｺ',
    'ｻ',
    'ｼ',
    'ｽ',
    'ｾ',
    'ｿ',
    'ﾀ',
    'ﾁ',
    'ﾂ',
    'ﾃ',
    'ﾄ',
    'ﾅ',
    'ﾆ',
    'ﾇ',
    'ﾈ',
    'ﾉ',
    'ﾊ',
    'ﾋ',
    'ﾌ',
    'ﾍ',
    'ﾎ',
    'ﾏ',
    'ﾐ',
    'ﾑ',
    'ﾒ',
    'ﾓ',
    'ﾔ',
    'ﾕ',
    'ﾖ',
    'ﾗ',
    'ﾘ',
    'ﾙ',
    'ﾚ',
    'ﾛ',
    'ﾜ',
    'ｦ',
    'ﾝ',
    'ｧ',
    'ｨ',
    'ｩ',
    'ｪ',
    'ｫ',
    'ｬ',
    'ｭ',
    'ｮ',
    'ｯ',
    'ｰ',
    // 以降は半角->半角の場合のみ使用
    'ﾟ',
    'ﾞ',
  ];
  let result = '';
  for (let i = 0; i < zenKata.length; i += 1) {
    const zenIndex = zen.indexOf(zenKata[i]);
    result += zenIndex >= 0 ? han[zenIndex] : '';

    const hanIndex = han.indexOf(zenKata[i]);
    result += hanIndex >= 0 ? han[hanIndex] : '';
  }

  return result;
};

/**
 * 日本円表記に変換
 * @param num 変換前数字
 * @returns 変換後円表記文字列
 */
export const numberFormat = (num: number): string => {
  return `${num.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' })}`;
};
